/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Building2, Users, Briefcase, ArrowUpRight, Send, Plus, FlaskConical, Calendar, Clock, Pencil, Trash2, Bell, CheckCircle2, UserCircle, Handshake, Copy, PhoneForwarded, TrendingUp, Loader2, DatabaseZap, CheckCircle, AlertCircle, ShieldAlert, Check, X, Flag, Mail, Info } from 'lucide-react';
import { StageCampaignModal } from '@/components/StageCampaignModal';
import { toast } from 'sonner';
import { OUTREACH_STATUS_COLORS, getStatusColor, statusLabel } from '@/lib/statusColors';

const API = '/api';

const STAGE_COLORS = {
  'Pipeline': 'bg-slate-100 text-slate-600',
  'Active': 'bg-sky-100 text-sky-700',
  'In Negotiation': 'bg-purple-100 text-purple-700',
  'On Hold': 'bg-amber-100 text-amber-700',
  'Closed': 'bg-green-100 text-green-700',
  'Dropped': 'bg-rose-100 text-rose-700',
  'Asset Under Evaluation': 'bg-amber-100 text-amber-700',
  'Meeting Scheduled': 'bg-violet-100 text-violet-700',
  'CDA Signed': 'bg-blue-100 text-blue-700',
  'Due Diligence': 'bg-indigo-100 text-indigo-700',
  'Deal Discussion': 'bg-purple-100 text-purple-700',
  'Closed - Successful': 'bg-emerald-100 text-emerald-700',
  'Declined': 'bg-rose-100 text-rose-700',
  'Awaiting Trial Data': 'bg-orange-100 text-orange-700',
};

const ASSET_STAGE_COLORS = {
  'Discovery': 'bg-zinc-100 text-zinc-700',
  'Pre-Clinical': 'bg-slate-100 text-slate-700',
  'IND': 'bg-sky-100 text-sky-700',
  'Phase 1': 'bg-blue-100 text-blue-700',
  'Phase 2': 'bg-violet-100 text-violet-700',
  'Phase 3': 'bg-purple-100 text-purple-700',
  'Clinical Stage': 'bg-fuchsia-100 text-fuchsia-700',
  'Commercial': 'bg-emerald-100 text-emerald-700',
  'Platform': 'bg-amber-100 text-amber-700',
};

const ASSET_STAGE_ORDER = ['Commercial', 'Phase 3', 'Phase 2', 'Phase 1', 'Clinical Stage', 'IND', 'Pre-Clinical', 'Other / Unstaged'];

const STATUS_BADGE = {
  'Not Started': 'bg-slate-100 text-slate-600',
  'Initiated': 'bg-sky-100 text-sky-700',
  'Active Discussion': 'bg-amber-100 text-amber-700',
  'Ghosted': 'bg-slate-200 text-slate-500',
  'Passed': 'bg-rose-100 text-rose-700',
  'Deal Closed': 'bg-emerald-100 text-emerald-700',
};

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(() => {
    try {
      const cached = localStorage.getItem('frx_dashboard_stats');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [statusDialog, setStatusDialog] = useState({ open: false, status: '', entries: [], loading: false });

  // Meeting state
  const emptyMeeting = { title: '', contact_name: '', contact_type: 'Pharma', company_name: '', meeting_date: '', meeting_time: '', notes: '' };
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [stageCampaignOpen, setStageCampaignOpen] = useState(false);
  const [meetingForm, setMeetingForm] = useState({ ...emptyMeeting });
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [deleteMeetingTarget, setDeleteMeetingTarget] = useState(null);

  // Reminder state
  const emptyReminder = { title: '', reminder_date: '', contact_name: '', contact_type: '', contact_email: '', company_name: '', notes: '' };
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [reminderForm, setReminderForm] = useState({ ...emptyReminder });
  const [editingReminder, setEditingReminder] = useState(null);
  const [deleteReminderTarget, setDeleteReminderTarget] = useState(null);

  // Follow-ups dialog state
  const [followupsDialogOpen, setFollowupsDialogOpen] = useState(false);
  // Which slice of reminders the dialog should show — switched by the chip the
  // user clicked: 'due' (overdue + today's), 'upcoming' (future), or 'notes'
  // (manual notes only — no auto_type).
  const [followupsDialogView, setFollowupsDialogView] = useState('due');

  // Database Cleanup state
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupScanning, setCleanupScanning] = useState(false);
  const [cleanupFlagged, setCleanupFlagged] = useState([]);
  const [cleanupSelected, setCleanupSelected] = useState(new Set());
  const [cleanupRemoving, setCleanupRemoving] = useState(false);

  // Contact suggestions for reminders
  const [reminderSuggestions, setReminderSuggestions] = useState([]);
  const [showReminderSuggestions, setShowReminderSuggestions] = useState(false);

  // Contact suggestions for meetings
  const [allContacts, setAllContacts] = useState([]);
  const [contactSuggestions, setContactSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Google Calendar state
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [calendarError, setCalendarError] = useState(null);

  // Database migration state
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [migrationRunning, setMigrationRunning] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [exportRunning, setExportRunning] = useState(false);
  const [exportResult, setExportResult] = useState(null);
  // Curated short list of biotechs the user manually flagged as "next up for
  // outreach" — populated from /api/biotech-companies/next-outreach/queue.
  const [nextOutreachQueue, setNextOutreachQueue] = useState([]);
  const [nextMatchingQueue, setNextMatchingQueue] = useState([]);
  const [nextOutreachLoading, setNextOutreachLoading] = useState(true);

  // Follow-up email — auto-send once per day on dashboard load
  useEffect(() => {
    const lastSent = localStorage.getItem('followup_email_last_sent');
    const today = new Date().toISOString().split('T')[0];
    if (lastSent !== today) {
      axios.post(`${API}/followups/send-daily-email`, {}, { withCredentials: true })
        .then(res => {
          if (res.data?.count > 0 && !res.data?.error) {
            localStorage.setItem('followup_email_last_sent', today);
          }
        })
        .catch(() => {});
    }
  }, []);

  const fetchStats = async (attempt = 0) => {
    try {
      const res = await axios.get(`${API}/dashboard/stats`, { withCredentials: true, timeout: 30000 });
      setStats(res.data);
      try { localStorage.setItem('frx_dashboard_stats', JSON.stringify(res.data)); } catch { /* ignore */ }
      setLoading(false);
    } catch (err) {
      console.error('Failed to load stats', err);
      if (attempt < 2) {
        setTimeout(() => fetchStats(attempt + 1), 2000 * (attempt + 1));
        return;
      }
      // All retries failed — fall back to the last-known stats so the counters
      // (Biotech Companies etc.) never blank out on a transient failure.
      try {
        const cached = localStorage.getItem('frx_dashboard_stats');
        if (cached) setStats(prev => prev || JSON.parse(cached));
      } catch { /* ignore */ }
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    axios.get(`${API}/biotech-companies/next-outreach/queue`, { withCredentials: true })
      .then(res => setNextOutreachQueue(res.data?.items || []))
      .catch(() => setNextOutreachQueue([]))
      .finally(() => setNextOutreachLoading(false));
    axios.get(`${API}/india-licensing/next-matching/queue`, { withCredentials: true })
      .then(res => setNextMatchingQueue(res.data?.items || []))
      .catch(() => setNextMatchingQueue([]));
    axios.get(`${API}/contacts/all`, { withCredentials: true })
      .then(res => setAllContacts(res.data))
      .catch(() => {});
    // Fetch Google Calendar events with timeout to prevent page blocking
    const calendarController = new AbortController();
    const calendarTimeout = setTimeout(() => calendarController.abort(), 8000);
    axios.get(`${API}/gmail/calendar/events?days=14`, { withCredentials: true, signal: calendarController.signal })
      .then(res => { setCalendarEvents(res.data?.events || []); setCalendarLoading(false); })
      .catch(err => {
        if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
          setCalendarError('error');
        } else {
          const detail = err.response?.data?.detail || '';
          if (err.response?.status === 401) {
            setCalendarError('connect');
          } else if (detail.includes('not been used') || detail.includes('disabled') || detail.includes('accessNotConfigured')) {
            setCalendarError('api_disabled');
          } else if (err.response?.status === 403) {
            setCalendarError('scope');
          } else {
            setCalendarError('error');
          }
        }
        setCalendarLoading(false);
      })
      .finally(() => clearTimeout(calendarTimeout));
    return () => { calendarController.abort(); clearTimeout(calendarTimeout); };
  }, []);

  const handleMeetingSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMeeting) {
        await axios.put(`${API}/meetings/${editingMeeting}`, meetingForm, { withCredentials: true });
        toast.success('Meeting updated');
      } else {
        await axios.post(`${API}/meetings`, meetingForm, { withCredentials: true });
        toast.success('Meeting added');
      }
      setMeetingDialogOpen(false);
      setMeetingForm({ ...emptyMeeting });
      setEditingMeeting(null);
      fetchStats();
    } catch {
      toast.error('Failed to save meeting');
    }
  };

  const handleEditMeeting = (m) => {
    setMeetingForm({ title: m.title, contact_name: m.contact_name || '', contact_type: m.contact_type || 'Pharma', company_name: m.company_name || '', meeting_date: m.meeting_date || '', meeting_time: m.meeting_time || '', notes: m.notes || '' });
    setEditingMeeting(m.id);
    setMeetingDialogOpen(true);
  };

  const handleDeleteMeeting = async () => {
    if (!deleteMeetingTarget) return;
    try {
      await axios.delete(`${API}/meetings/${deleteMeetingTarget.id}`, { withCredentials: true });
      toast.success('Meeting deleted');
      setDeleteMeetingTarget(null);
      fetchStats();
    } catch {
      toast.error('Failed to delete meeting');
    }
  };

  const handleContactSearch = (val) => {
    setMeetingForm(p => ({ ...p, contact_name: val }));
    if (val.length >= 2) {
      const q = val.toLowerCase();
      const matches = allContacts.filter(c => c.name.toLowerCase().includes(q)).slice(0, 8);
      setContactSuggestions(matches);
      setShowSuggestions(matches.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const selectContact = (contact) => {
    setMeetingForm(p => ({ ...p, contact_name: contact.name, contact_type: contact.type, company_name: contact.company }));
    setShowSuggestions(false);
  };

  // Reminder handlers
  const handleReminderSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingReminder) {
        await axios.put(`${API}/reminders/${editingReminder}`, reminderForm, { withCredentials: true });
        toast.success('Note updated');
      } else {
        await axios.post(`${API}/reminders`, reminderForm, { withCredentials: true });
        toast.success('Note added');
      }
      setReminderDialogOpen(false);
      setReminderForm({ ...emptyReminder });
      setEditingReminder(null);
      fetchStats();
    } catch {
      toast.error('Failed to save note');
    }
  };

  const handleEditReminder = (r) => {
    setReminderForm({ title: r.title, reminder_date: r.reminder_date || '', contact_name: r.contact_name || '', contact_type: r.contact_type || '', contact_email: r.contact_email || '', company_name: r.company_name || '', notes: r.notes || '' });
    setEditingReminder(r.id);
    setReminderDialogOpen(true);
  };

  const handleMarkReminderDone = async (id) => {
    try {
      await axios.patch(`${API}/reminders/${id}/done`, {}, { withCredentials: true });
      toast.success('Note completed');
      fetchStats();
    } catch {
      toast.error('Failed to update note');
    }
  };

  const handleDeleteReminder = async () => {
    if (!deleteReminderTarget) return;
    try {
      await axios.delete(`${API}/reminders/${deleteReminderTarget.id}`, { withCredentials: true });
      toast.success('Note deleted');
      setDeleteReminderTarget(null);
      fetchStats();
    } catch {
      toast.error('Failed to delete note');
    }
  };

  const handleConnectGmail = async () => {
    try {
      const r = await axios.get(`${API}/oauth/gmail/connect`, { withCredentials: true });
      window.location.href = r.data.auth_url;
    } catch {
      toast.error('Failed to start Gmail connection');
    }
  };

  const handleReminderContactSearch = (val) => {
    setReminderForm(p => ({ ...p, contact_name: val }));
    if (val.length >= 2) {
      const q = val.toLowerCase();
      const matches = allContacts.filter(c => c.name.toLowerCase().includes(q)).slice(0, 8);
      setReminderSuggestions(matches);
      setShowReminderSuggestions(matches.length > 0);
    } else {
      setShowReminderSuggestions(false);
    }
  };

  const selectReminderContact = (contact) => {
    setReminderForm(p => ({ ...p, contact_name: contact.name, contact_type: contact.type, contact_email: contact.email || '', company_name: contact.company }));
    setShowReminderSuggestions(false);
  };

  const checkMigrationStatus = async () => {
    setMigrationLoading(true);
    try {
      const res = await axios.get(`${API}/db/migration-status`, { withCredentials: true });
      setMigrationStatus(res.data);
    } catch {
      setMigrationStatus(null);
    } finally {
      setMigrationLoading(false);
    }
  };

  const runExport = async () => {
    setExportRunning(true);
    setExportResult(null);
    try {
      const res = await axios.post(`${API}/db/run-export`, {}, { withCredentials: true });
      setExportResult(res.data);
      toast.success(`Export complete — ${res.data.total_documents} documents snapshot saved`);
      checkMigrationStatus();
    } catch (err) {
      toast.error('Export failed: ' + (err.response?.data?.detail || 'Unknown error'));
    } finally {
      setExportRunning(false);
    }
  };

  const runMigration = async () => {
    setMigrationRunning(true);
    setMigrationResult(null);
    try {
      const res = await axios.post(`${API}/db/run-import`, {}, { withCredentials: true });
      setMigrationResult(res.data);
      if (res.data.errors?.length > 0) {
        toast.error(`Migration had errors: ${res.data.errors.join(', ')}`);
      } else {
        toast.success(`Migration complete — ${res.data.total_imported} documents imported`);
      }
      fetchStats();
    } catch (err) {
      toast.error('Migration failed: ' + (err.response?.data?.detail || 'Unknown error'));
    } finally {
      setMigrationRunning(false);
    }
  };

  const openStatusDetail = (status) => {
    // Land on the familiar Outreach interface (same table the user works
    // in every day), filtered to this status. The Outreach page de-dupes
    // by (asset × biotech × pharma) so its total matches the chip count.
    window.open(`/outreach?status=${encodeURIComponent(status)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-full">
        <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const metricCards = [
    { label: 'Biotech Companies', value: stats?.biotech_companies || 0, icon: Building2, color: 'text-sky-600', bg: 'bg-sky-50', link: '/biotech' },
    // Feb 2026 · NDDS restored as a first-class category tile per user
    // request. Total Biotech Companies already includes NDDS (see
    // out_licensing_scope.py — "ndds" removed from EXCLUDED_CATEGORIES),
    // so this tile is a drill-down into the NDDS slice. Link deep-links
    // to the Out-Licensers list with the category filter pre-applied.
    { label: 'NDDS (New Drug Delivery)', value: stats?.ndds_count || 0, icon: FlaskConical, color: 'text-fuchsia-600', bg: 'bg-fuchsia-50', link: '/biotech?category=ndds' },
    { label: 'Pharma Companies', value: stats?.pharma_companies || 0, icon: Building2, color: 'text-indigo-600', bg: 'bg-indigo-50', link: '/company-profiles?category=pharma' },
    { label: 'Commercial Partners', value: stats?.commercial_partners || 0, icon: Building2, color: 'text-amber-600', bg: 'bg-amber-50', link: '/company-profiles?category=regional_distributor' },
    { label: 'Total Assets', value: stats?.total_assets || 0, icon: FlaskConical, color: 'text-orange-600', bg: 'bg-orange-50', link: '/assets' },
    { label: 'Pharma Contacts', value: stats?.pharma_contacts || 0, icon: Users, color: 'text-emerald-600', bg: 'bg-emerald-50', link: '/pharma' },
  ];


  const scanForNonTargets = async () => {
    setCleanupScanning(true);
    setCleanupFlagged([]);
    setCleanupSelected(new Set());
    try {
      const res = await axios.get(`${API}/biotech-companies/scan/non-target`, { withCredentials: true });
      setCleanupFlagged(res.data.flagged || []);
      if ((res.data.flagged || []).length === 0) toast.success('Database is clean — no non-target entities found');
    } catch { toast.error('Scan failed'); }
    finally { setCleanupScanning(false); }
  };

  const removeNonTargets = async () => {
    if (cleanupSelected.size === 0) return;
    setCleanupRemoving(true);
    try {
      const items = cleanupFlagged.filter(f => cleanupSelected.has(f.id)).map(f => ({ id: f.id, collection: f.collection }));
      await axios.delete(`${API}/biotech-companies/bulk-remove`, { data: { items }, withCredentials: true });
      toast.success(`Removed ${items.length} non-target entit${items.length === 1 ? 'y' : 'ies'}`);
      setCleanupFlagged(prev => prev.filter(f => !cleanupSelected.has(f.id)));
      setCleanupSelected(new Set());
      fetchStats();
    } catch { toast.error('Failed to remove'); }
    finally { setCleanupRemoving(false); }
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="dashboard-page">
      <div className="mb-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Welcome back{user?.name ? `, ${user.name}` : ''}
            </h1>
          </div>
        </div>
        {/* Activity Summary Strip — includes Follow-ups */}
        {(() => {
          const today = new Date().toISOString().split('T')[0];
          const followupsDue = stats?.active_reminders?.filter(r => (r.auto_type === 'pharma_followup' || r.auto_type === 'account_intro_followup') && r.reminder_date <= today).length || 0;
          const upcomingFollowups = stats?.active_reminders?.filter(r => (r.auto_type === 'pharma_followup' || r.auto_type === 'account_intro_followup') && r.reminder_date > today).length || 0;
          const notes = stats?.active_reminders?.filter(r => !r.auto_type).length || 0;
          const calEvents = calendarEvents?.length || 0;
          const items = [
            { label: 'Pending Follow-ups', count: followupsDue, color: followupsDue > 0 ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-slate-50 text-slate-500 border-slate-200', icon: PhoneForwarded, onClick: () => { setFollowupsDialogView('due'); setFollowupsDialogOpen(true); } },
            { label: 'Notes', count: notes, color: 'bg-teal-50 text-teal-700 border-teal-200', icon: Bell, onClick: () => { setFollowupsDialogView('notes'); setFollowupsDialogOpen(true); } },
            { label: 'Upcoming Meetings', count: calEvents, color: calEvents > 0 ? 'bg-violet-50 text-violet-700 border-violet-200' : 'bg-slate-50 text-slate-500 border-slate-200', icon: Calendar },
          ];
          return (
            <div className="flex flex-wrap gap-3" data-testid="activity-summary">
              {items.map((item) => (
                <div key={item.label} className={`flex items-center gap-2 px-3.5 py-2 rounded-lg border ${item.color} transition-all ${item.onClick ? 'cursor-pointer hover:opacity-80' : ''}`} onClick={item.onClick}>
                  <item.icon className="w-3.5 h-3.5" />
                  <span className="text-sm font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{item.count}</span>
                  <span className="text-xs font-medium">{item.label}</span>
                </div>
              ))}
            </div>
          );
        })()}
      </div>
      {/* Top row: Next Outreach Queue (left) + Upcoming Meetings (right) —
          both panels share a 400px maxHeight with internal scroll so the
          dashboard layout stays compact regardless of queue length. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5 items-stretch">
        {/* Next Outreach Queue — curated short list (separate from "Pending Outreach" pool) */}
        <Card className="border border-emerald-200 shadow-sm flex flex-col" style={{ maxHeight: '300px' }} data-testid="next-outreach-queue-card">
        <CardHeader className="pb-3 shrink-0">
          <CardTitle className="text-base flex items-center gap-2 flex-wrap" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Flag className="w-4 h-4 fill-emerald-500 text-emerald-600 shrink-0" />
            <span>Lined up for next outreach</span>
            {!nextOutreachLoading && (
              <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200 shrink-0">
                {nextOutreachQueue.length}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 flex-1 overflow-y-auto" data-testid="next-outreach-queue-scroll">
          {nextOutreachLoading ? (
            <div className="py-6 text-center text-xs text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading queue...
            </div>
          ) : nextOutreachQueue.length === 0 ? (
            <div className="py-6 text-center text-xs text-slate-400">
              No biotechs lined up yet. Open any biotech detail page and tap the <Flag className="w-3 h-3 inline mx-0.5 text-emerald-500" /> flag in the header to add them here.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              {nextOutreachQueue.map(item => (
                <div
                  key={item.company_id}
                  className="group flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-white hover:bg-emerald-50/30 hover:border-emerald-200 transition-colors cursor-pointer"
                  onClick={() => window.open(`/biotech/${item.company_id}`, '_blank', 'noopener')}
                  data-testid={`next-outreach-item-${item.company_id}`}
                >
                  <div className="w-7 h-7 rounded-md bg-emerald-50 flex items-center justify-center shrink-0 mt-0.5">
                    <Flag className="w-3.5 h-3.5 fill-emerald-500 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-slate-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {item.company_name}
                      </p>
                      {item.country && (
                        <span className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{item.country}</span>
                      )}
                      {item.primary_contact_email && (
                        <a
                          href={`mailto:${item.primary_contact_email}`}
                          onClick={e => e.stopPropagation()}
                          className="text-[10px] text-slate-500 hover:text-emerald-700 flex items-center gap-0.5"
                          title={`Email ${item.primary_contact_name || item.primary_contact_email}`}
                        >
                          <Mail className="w-2.5 h-2.5" /> {item.primary_contact_name || item.primary_contact_email.split('@')[0]}
                        </a>
                      )}
                    </div>
                    {item.note && (
                      <p className="text-xs text-slate-600 mt-1 leading-snug line-clamp-2">{item.note}</p>
                    )}
                    <p className="text-[10px] text-slate-400 mt-1">
                      Flagged {item.flagged_at ? new Date(item.flagged_at).toLocaleDateString() : '—'}{item.flagged_by ? ` · ${item.flagged_by.split('@')[0]}` : ''}
                    </p>
                  </div>
                  <ArrowUpRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-emerald-600 transition-colors shrink-0 mt-1" />
                </div>
              ))}
            </div>
          )}
        </CardContent>
        </Card>

        {/* Accounts lined up for matching — manual flag on the account page */}
        <Card className="border border-emerald-200 shadow-sm flex flex-col" style={{ maxHeight: '300px' }} data-testid="next-matching-queue-card">
          <CardHeader className="pb-3 shrink-0">
            <CardTitle className="text-base flex items-center gap-2 flex-wrap" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Flag className="w-4 h-4 fill-emerald-500 text-emerald-600 shrink-0" />
              <span>Accounts lined up for matching</span>
              <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200 shrink-0">{nextMatchingQueue.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 flex-1 overflow-y-auto" data-testid="next-matching-queue-scroll">
            {nextMatchingQueue.length === 0 ? (
              <div className="py-6 text-center text-xs text-slate-400">
                No accounts lined up. Open an account and tap <span className="font-medium text-slate-600">Run matching next</span> to add it here.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-2">
                {nextMatchingQueue.map(item => (
                  <div key={item.project_id}
                    className="group flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-white hover:bg-emerald-50/30 hover:border-emerald-200 transition-colors cursor-pointer"
                    onClick={() => navigate(`/accounts/${item.project_id}`)}
                    data-testid={`next-matching-item-${item.project_id}`}>
                    <div className="w-7 h-7 rounded-md bg-emerald-50 flex items-center justify-center shrink-0 mt-0.5">
                      <Briefcase className="w-3.5 h-3.5 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {item.client_company}{item.client_name ? <span className="font-normal text-slate-500"> · {item.client_name}</span> : null}
                      </p>
                      {item.note && <p className="text-xs text-slate-600 mt-1 leading-snug line-clamp-2">{item.note}</p>}
                      <p className="text-[10px] text-slate-400 mt-1">
                        Flagged {item.flagged_at ? new Date(item.flagged_at).toLocaleDateString() : '—'}{item.last_matching_run_at ? ` · last run ${new Date(item.last_matching_run_at).toLocaleDateString()}` : ' · never run'}
                      </p>
                    </div>
                    <ArrowUpRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-emerald-600 transition-colors shrink-0 mt-1" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right: Google Calendar */}
        <Card className="border border-slate-200 shadow-sm flex flex-col" style={{ maxHeight: '300px' }} data-testid="google-calendar">
          <CardHeader className="pb-3 shrink-0">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Calendar className="w-4 h-4 text-slate-400" />
                Google Calendar
              </CardTitle>
              {calendarEvents.length > 0 && (
                <Badge variant="secondary" className="text-xs">{calendarEvents.length} events</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="flex-1 min-h-0 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
            {calendarLoading ? (
              <div className="text-center py-10">
                <div className="w-6 h-6 border-2 border-slate-200 border-t-[#00709B] rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-slate-400">Loading calendar...</p>
              </div>
            ) : calendarError === 'connect' ? (
              <div className="text-center py-10 flex flex-col items-center justify-center">
                <Calendar className="w-10 h-10 text-slate-300 mb-3" />
                <p className="text-sm text-slate-500 mb-2">Gmail not connected</p>
                <Button variant="outline" size="sm" onClick={handleConnectGmail} data-testid="connect-gmail-btn">
                  Connect Gmail to view calendar
                </Button>
              </div>
            ) : calendarError === 'scope' ? (
              <div className="text-center py-10 flex flex-col items-center justify-center">
                <Calendar className="w-10 h-10 text-amber-300 mb-3" />
                <p className="text-sm text-slate-500 mb-2">Calendar access needed</p>
                <p className="text-xs text-slate-400 mb-3">Please reconnect Gmail to grant calendar permissions</p>
                <Button variant="outline" size="sm" onClick={handleConnectGmail} data-testid="reconnect-gmail-btn">
                  Reconnect Gmail
                </Button>
              </div>
            ) : calendarError === 'api_disabled' ? (
              <div className="text-center py-8 flex flex-col items-center justify-center">
                <Calendar className="w-10 h-10 text-amber-300 mb-3" />
                <p className="text-sm font-medium text-slate-700 mb-1">Google Calendar API needs to be enabled</p>
                <p className="text-xs text-slate-400 mb-3 max-w-[280px]">Enable the Calendar API in your Google Cloud Console, then reconnect.</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => window.open('https://console.developers.google.com/apis/api/calendar-json.googleapis.com/overview?project=66714439357', '_blank')} data-testid="enable-calendar-api-btn">
                    Enable Calendar API
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleConnectGmail} data-testid="reconnect-gmail-btn">
                    Reconnect
                  </Button>
                </div>
              </div>
            ) : calendarEvents.length > 0 ? (
              <div className="space-y-2" data-testid="calendar-events-list">
                {(() => {
                  // Group events by matched company so repeat meetings with the
                  // same biotech/pharma (e.g. IDNS, Alcon, Anlong) collapse into
                  // a single row with a count and a combined list.
                  const groups = [];
                  const byKey = new Map();
                  calendarEvents.forEach(evt => {
                    const key = evt.matched_company || `__unique_${evt.id}`;
                    if (byKey.has(key)) {
                      byKey.get(key).push(evt);
                    } else {
                      const arr = [evt];
                      byKey.set(key, arr);
                      groups.push({ key, companyName: evt.matched_company || null, events: arr });
                    }
                  });
                  // Sort groups by earliest event start
                  groups.forEach(g => g.events.sort((a, b) => new Date(a.start) - new Date(b.start)));
                  groups.sort((a, b) => new Date(a.events[0].start) - new Date(b.events[0].start));
                  return groups;
                })().map((group) => {
                  const evt = group.events[0];
                  const startDate = new Date(evt.start);
                  const now = new Date();
                  const isToday = startDate.toDateString() === now.toDateString();
                  const isTomorrow = startDate.toDateString() === new Date(now.getTime() + 86400000).toDateString();
                  const isPast = startDate < now;
                  const startTime = evt.all_day ? 'All day' : startDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                  const isMerged = group.events.length > 1;
                  return (
                    <a
                      key={group.key}
                      href={evt.html_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={isMerged ? group.events.map(e => `${new Date(e.start).toLocaleString()} — ${e.title}`).join('\n') : undefined}
                      className={`flex items-center justify-between py-2.5 px-3 rounded-md ${isToday ? 'bg-sky-50 border border-sky-200' : 'bg-slate-50'} hover:bg-slate-100 transition-colors group block no-underline`}
                      data-testid={`calendar-event-${evt.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${isPast && !isToday ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
                          {group.companyName ? group.companyName : evt.title}
                          {isMerged && (
                            <span className="ml-1.5 inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200">
                              {group.events.length} meetings
                            </span>
                          )}
                        </p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {group.companyName && !isMerged && evt.title && evt.title !== group.companyName && (
                            <span className="text-xs text-slate-500 truncate max-w-[200px]">{evt.title}</span>
                          )}
                          {evt.location && <span className="text-xs text-slate-500 truncate max-w-[150px]">{evt.location}</span>}
                          {evt.attendees?.length > 0 && (
                            <span className="text-xs text-slate-400">{evt.attendees.length} attendee{evt.attendees.length !== 1 ? 's' : ''}</span>
                          )}
                          {evt.conference_link && (
                            <Badge variant="secondary" className="text-[10px] bg-blue-50 text-blue-600">
                              {evt.conference_link.includes('zoom') ? 'Zoom' : evt.conference_link.includes('meet.google') ? 'Meet' : 'Video'}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="text-right ml-3 shrink-0">
                        <p className={`text-xs font-medium ${isToday ? 'text-sky-700' : 'text-slate-700'}`}>
                          {isToday ? 'Today' : isTomorrow ? 'Tomorrow' : startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </p>
                        <p className="text-[10px] text-slate-400 flex items-center gap-0.5 justify-end">
                          <Clock className="w-2.5 h-2.5" />{startTime}
                        </p>
                      </div>
                    </a>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-10 flex flex-col items-center justify-center">
                <Calendar className="w-10 h-10 text-slate-300 mb-3" />
                <p className="text-sm text-slate-500">No upcoming events in the next 14 days</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>


      {/* Row 1: KPIs (Active Conversations + Active Deals) merged into
          BD Funnel panel below — see "BD Funnel" header pills. Per user
          2026-02: "the whole idea is to have a one single BD funnel data". */}

      {/* Row 2 (Off-Patent / Tail card) removed Jul 2026 — tail assets purged
          from scope; dashboard no longer surfaces the module. */}

      {/* Row 3: Database Counts — Biotech | Pharma | Commercial Partners | Assets | Pharma Contacts */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5" data-testid="dashboard-metric-row">
        {metricCards.map((m, i) => (
          <Card
            key={i}
            className={`border border-slate-200 shadow-sm transition-all duration-300 ${m.link ? 'hover:shadow-md cursor-pointer group' : ''}`}
            onClick={() => m.link && navigate(m.link)}
            data-testid={`metric-${m.label.toLowerCase().replace(/ /g, '-')}`}
          >
            <CardContent className="p-3.5">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-500 tracking-wide uppercase leading-tight" style={{ fontFamily: 'Public Sans, sans-serif', fontSize: '10px', letterSpacing: '0.05em' }}>{m.label}</p>
                  <div className="flex items-baseline gap-1.5 mt-1.5">
                    <p className="text-2xl font-bold text-slate-900 leading-none" style={{ fontFamily: 'Manrope, sans-serif' }}>{m.value}</p>
                  </div>
                  {m.active > 0 && (
                    <p className="text-[11px] text-emerald-600 font-semibold mt-1" style={{ fontFamily: 'Public Sans, sans-serif' }}>{m.active} {m.activeLabel || 'active'}</p>
                  )}
                </div>
                <div className={`w-8 h-8 rounded-lg ${m.bg} flex items-center justify-center shrink-0 ${m.link ? 'group-hover:scale-110' : ''} transition-transform`}>
                  <m.icon className={`w-4 h-4 ${m.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Row 4: BD Funnel + Asset Stages */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Outreach Overview */}
        <Card className="border border-slate-200 shadow-sm" data-testid="pipeline-overview">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Briefcase className="w-4 h-4 text-slate-400" />
              BD Funnel
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button className="text-slate-400 hover:text-slate-600" data-testid="outreach-overview-info"><Info className="w-3.5 h-3.5" /></button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs text-xs">
                    Unified BD pipeline — combines direct outreach + accounts-driven engagement. Each stage shows # of unique <strong>(asset × biotech × pharma)</strong> conversations at that stage. Click any stage to drill in.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardTitle>
            {/* Summary pills — All Conversations + Active Conversations
                shown as a left/right pair at the top of the funnel.
                "Active Deals" removed earlier (was duplicating Deal
                Discussion + Deal Closed rows already in the funnel). */}
            <div className="grid grid-cols-2 gap-2 mt-3" data-testid="bd-funnel-summary">
              <a
                href="/outreach"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 rounded-md bg-slate-50 hover:bg-slate-100 transition-colors group justify-start"
                data-testid="bd-funnel-summary-all"
              >
                <Briefcase className="w-3.5 h-3.5 text-slate-500" />
                <span className="text-[11px] font-medium text-slate-600 uppercase tracking-wide" style={{ fontFamily: 'Public Sans, sans-serif' }}>All Conversations</span>
                <span className="text-base font-bold text-slate-900 tabular-nums ml-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>{stats?.total_outreach || 0}</span>
                <ArrowUpRight className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
              <a
                href="/outreach?filter=active"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 rounded-md bg-violet-50 hover:bg-violet-100 transition-colors group justify-start"
                data-testid="bd-funnel-summary-active"
              >
                <Send className="w-3.5 h-3.5 text-violet-600" />
                <span className="text-[11px] font-medium text-violet-700 uppercase tracking-wide" style={{ fontFamily: 'Public Sans, sans-serif' }}>Active Conversations</span>
                <span className="text-base font-bold text-slate-900 tabular-nums ml-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>{stats?.active_conversations || 0}</span>
                <ArrowUpRight className="w-3 h-3 text-violet-500 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            </div>
          </CardHeader>
          <CardContent>
            {stats?.outreach_by_status && Object.keys(stats.outreach_by_status).length > 0 ? (
              <div className="space-y-1">
                {(() => {
                  // Asset-level BD funnel — top of funnel at the top, deal-
                  // closing at the bottom. Each stage shows count, the bar
                  // is proportional to the top-of-funnel value so you can
                  // *see* the drop-off, and the conversion % from previous
                  // stage is rendered between rows so the leak is obvious.
                  // Aug 2026 (v2) · Trimmed funnel per user directive:
                  // "Remove Introduction Made, Email Acknowledged,
                  // Already in Touch and CDA Signed — keep the funnel
                  // from Asset Under Evaluation through Deal Closed."
                  const FUNNEL = [
                    'Asset Under Evaluation',
                    'Meeting Requested',
                    'Meeting Scheduled',
                    'CDA in Progress',
                    'CDA Signed',
                    'Data Shared / Data Room Access',
                    'Awaiting Trial Data',
                    'Due Diligence',
                    'Deal Discussion',
                    'Term Sheet Shared',
                    'Deal Closed',
                  ];
                  const OFF_FUNNEL = ['Declined', 'Deal Lost', 'On Hold', 'Ghosted'];
                  const counts = stats.outreach_by_status || {};
                  const assetCounts = stats.assets_by_status || {};
                  // Asset-level stages where the user wants the funnel to
                  // surface the # of UNIQUE ASSETS at that stage (e.g. 1
                  // asset under evaluation = IMM0306) rather than the
                  // raw conversation count (8 evaluators × 4 pharmas = 48
                  // conversations). Conversation count is kept as a
                  // sub-label so it's still one click away. Meeting
                  // Scheduled is excluded — many meetings have no asset
                  // locked in yet (Akums × KinoPharma), so the
                  // conversation count is the more meaningful unit there.
                  const STAGES_USE_ASSET_COUNT = new Set([
                    'Asset Under Evaluation',
                    'Data Shared / Data Room Access',
                    'Awaiting Trial Data',
                    'Due Diligence',
                    'Deal Discussion',
                    'Term Sheet Shared',
                    'Deal Closed',
                  ]);
                  const displayCount = (stage) =>
                    STAGES_USE_ASSET_COUNT.has(stage) ? (assetCounts[stage] || 0) : (counts[stage] || 0);
                  // Feb 2026 (v6) — user rule: "every tab from dashboard
                  // should be giving me real insights if i click on them,
                  // otherwise no point." Hide stages that have zero
                  // conversations/assets so the funnel never presents a
                  // dead-end click. When the pipeline is empty at a
                  // stage we simply don't show that stage until at least
                  // one conversation lands in it.
                  const visibleFunnel = FUNNEL.filter(s => displayCount(s) > 0);
                  const visibleOffFunnel = OFF_FUNNEL.filter(s => (counts[s] || 0) > 0);
                  const maxCount = Math.max(1, ...visibleFunnel.map(s => displayCount(s)));
                  return (
                    <>
                      {FUNNEL.map((stage, idx) => {
                        const count = displayCount(stage);
                        const subLabel = STAGES_USE_ASSET_COUNT.has(stage)
                          ? `${counts[stage] || 0} conversation${(counts[stage] || 0) === 1 ? '' : 's'}`
                          : null;
                        const widthPct = (count / maxCount) * 100;
                        return (
                          <React.Fragment key={stage}>
                            <a
                              href={`/outreach?status=${encodeURIComponent(stage)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="relative group cursor-pointer block"
                              data-testid={`funnel-stage-${stage.toLowerCase().replace(/ /g, '-')}`}
                            >
                              <div className="flex items-center gap-3 py-2 px-3 rounded-md hover:bg-slate-50 transition-colors">
                                <Badge variant="secondary" className={`text-[11px] shrink-0 w-44 justify-start ${getStatusColor(stage)}`}>
                                  {statusLabel(stage)}
                                </Badge>
                                <div className="flex-1 relative h-6 bg-slate-100 rounded overflow-hidden">
                                  <div
                                    className={`absolute inset-y-0 left-0 ${count > 0 ? getStatusColor(stage).replace('text-', 'bg-').split(' ')[0].replace('-100','-400') : ''}`}
                                    style={{ width: `${widthPct}%`, transition: 'width .35s ease' }}
                                  />
                                </div>
                                <div className="w-20 text-right">
                                  <div className="text-sm font-bold text-slate-900 tabular-nums" style={{ fontFamily: 'Manrope, sans-serif' }}>{count.toLocaleString()}</div>
                                  {subLabel && <div className="text-[10px] text-slate-400 leading-none mt-0.5">{subLabel}</div>}
                                </div>
                                <ArrowUpRight className="w-3.5 h-3.5 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                            </a>
                          </React.Fragment>
                        );
                      })}

                      {/* Off-funnel drop-offs */}
                      {OFF_FUNNEL.some(s => counts[s]) && (
                        <div className="mt-4 pt-3 border-t border-slate-100">
                          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-2 px-3">Off-funnel</p>
                          <div className="flex flex-wrap gap-2 px-3">
                            {OFF_FUNNEL.filter(s => counts[s]).map(stage => (
                              <a
                                key={stage}
                                href={`/outreach?status=${encodeURIComponent(stage)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-50 hover:bg-slate-100 text-xs transition-colors"
                                data-testid={`funnel-offfunnel-${stage.toLowerCase().replace(/ /g, '-')}`}
                              >
                                <span className={`px-1.5 py-0.5 rounded text-[10px] ${getStatusColor(stage)}`}>{stage}</span>
                                <span className="font-semibold text-slate-700 tabular-nums">{counts[stage].toLocaleString()}</span>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            ) : (
              <div className="text-center py-12">
                <Briefcase className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">No outreach activity yet.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Asset Stage Overview */}
        <Card className="border border-slate-200 shadow-sm" data-testid="asset-stage-overview">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold flex items-center justify-between" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <span className="flex items-center gap-2">
                <FlaskConical className="w-4 h-4 text-slate-400" />
                Asset Stages
              </span>
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setStageCampaignOpen(true)} data-testid="open-stage-campaign-btn">
                <Mail className="w-3.5 h-3.5 mr-1" /> Email companies
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {ASSET_STAGE_ORDER.map((stage) => (
                <a
                  key={stage}
                  href={`/assets?stage=${encodeURIComponent(stage)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between py-2 px-3 rounded-md bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer"
                  data-testid={`stage-${stage.toLowerCase().replace(/ /g, '-')}`}
                >
                  <Badge variant="secondary" className={`text-xs ${ASSET_STAGE_COLORS[stage] || 'bg-slate-100 text-slate-600'}`}>
                    {stage}
                  </Badge>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{(stats?.companies_by_stage || {})[stage]?.assets || (typeof (stats?.companies_by_stage || {})[stage] === 'number' ? (stats?.companies_by_stage || {})[stage] : 0)}</span>
                    <span className="text-[10px] text-slate-400">{(stats?.companies_by_stage || {})[stage]?.companies ? `${(stats?.companies_by_stage || {})[stage].companies} co.` : ''}</span>
                    <ArrowUpRight className="w-3.5 h-3.5 text-slate-400" />
                  </div>
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>


      {/* Stage Campaign (JCA-Matrix service pitch) */}
      <StageCampaignModal open={stageCampaignOpen} onOpenChange={setStageCampaignOpen} />

      {/* Add/Edit Reminder Dialog */}
      <Dialog open={reminderDialogOpen} onOpenChange={(open) => { setReminderDialogOpen(open); if (!open) { setEditingReminder(null); setReminderForm({ ...emptyReminder }); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{editingReminder ? 'Edit Note' : 'Add Note'}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Add a note with optional contact details and date.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleReminderSubmit} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Reminder *</Label>
              <Input value={reminderForm.title} onChange={e => setReminderForm(p => ({ ...p, title: e.target.value }))} required placeholder="e.g., Meet Sarah at Bio JPM" data-testid="reminder-title-input" />
            </div>
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input type="date" value={reminderForm.reminder_date} onChange={e => setReminderForm(p => ({ ...p, reminder_date: e.target.value }))} required data-testid="reminder-date-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2 relative">
                <Label>Contact Name</Label>
                <Input
                  value={reminderForm.contact_name}
                  onChange={e => handleReminderContactSearch(e.target.value)}
                  onFocus={() => reminderForm.contact_name.length >= 2 && reminderSuggestions.length > 0 && setShowReminderSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowReminderSuggestions(false), 200)}
                  placeholder="Start typing..."
                  autoComplete="off"
                  data-testid="reminder-contact-input"
                />
                {showReminderSuggestions && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="reminder-contact-suggestions">
                    {reminderSuggestions.map((c, i) => {
                      const tc = c.type === 'Pharma' ? 'text-sky-600' : 'text-amber-600';
                      return (
                        <div key={i} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50 cursor-pointer" onMouseDown={() => selectReminderContact(c)}>
                          <div>
                            <p className="text-sm font-medium text-slate-800">{c.name}</p>
                            {c.company && <p className="text-xs text-slate-400">{c.company}</p>}
                          </div>
                          <span className={`text-[10px] font-medium ${tc}`}>{c.type}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Company</Label>
                <Input value={reminderForm.company_name} onChange={e => setReminderForm(p => ({ ...p, company_name: e.target.value }))} placeholder="Company name" data-testid="reminder-company-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={reminderForm.contact_email} onChange={e => setReminderForm(p => ({ ...p, contact_email: e.target.value }))} placeholder="contact@example.com" data-testid="reminder-email-input" />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={reminderForm.notes} onChange={e => setReminderForm(p => ({ ...p, notes: e.target.value }))} placeholder="Additional details..." className="min-h-[60px]" data-testid="reminder-notes-input" />
            </div>
            <div className="flex justify-end gap-3 pt-1">
              <Button type="button" variant="outline" onClick={() => setReminderDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-amber-600 text-white hover:bg-amber-700" data-testid="reminder-submit-btn">{editingReminder ? 'Update' : 'Add Reminder'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Reminder Confirm */}
      <AlertDialog open={!!deleteReminderTarget} onOpenChange={(open) => !open && setDeleteReminderTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Note</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete "{deleteReminderTarget?.title}"?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteReminder} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-reminder">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add/Edit Meeting Dialog */}
      <Dialog open={meetingDialogOpen} onOpenChange={(open) => { setMeetingDialogOpen(open); if (!open) { setEditingMeeting(null); setMeetingForm({ ...emptyMeeting }); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{editingMeeting ? 'Edit Meeting' : 'Schedule Meeting'}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Add meeting details with a contact.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleMeetingSubmit} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Meeting Title *</Label>
              <Input value={meetingForm.title} onChange={e => setMeetingForm(p => ({ ...p, title: e.target.value }))} required placeholder="e.g., Intro call with pharma BD team" data-testid="meeting-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input type="date" value={meetingForm.meeting_date} onChange={e => setMeetingForm(p => ({ ...p, meeting_date: e.target.value }))} required data-testid="meeting-date-input" />
              </div>
              <div className="space-y-2">
                <Label>Time</Label>
                <Input type="time" value={meetingForm.meeting_time} onChange={e => setMeetingForm(p => ({ ...p, meeting_time: e.target.value }))} data-testid="meeting-time-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2 relative">
                <Label>Contact Name</Label>
                <Input
                  value={meetingForm.contact_name}
                  onChange={e => handleContactSearch(e.target.value)}
                  onFocus={() => meetingForm.contact_name.length >= 2 && contactSuggestions.length > 0 && setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  placeholder="Start typing to search..."
                  autoComplete="off"
                  data-testid="meeting-contact-input"
                />
                {showSuggestions && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="contact-suggestions">
                    {contactSuggestions.map((c, i) => {
                      const typeColor = c.type === 'Pharma' ? 'text-sky-600' : 'text-amber-600';
                      return (
                        <div
                          key={i}
                          className="flex items-center justify-between px-3 py-2 hover:bg-slate-50 cursor-pointer"
                          onMouseDown={() => selectContact(c)}
                          data-testid={`suggestion-${i}`}
                        >
                          <div>
                            <p className="text-sm font-medium text-slate-800">{c.name}</p>
                            {c.company && <p className="text-xs text-slate-400">{c.company}</p>}
                          </div>
                          <span className={`text-[10px] font-medium ${typeColor}`}>{c.type}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Contact Type</Label>
                <Select value={meetingForm.contact_type} onValueChange={v => setMeetingForm(p => ({ ...p, contact_type: v }))}>
                  <SelectTrigger data-testid="meeting-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pharma">Pharma</SelectItem>
                    <SelectItem value="Biotech">Biotech</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Company</Label>
              <Input value={meetingForm.company_name} onChange={e => setMeetingForm(p => ({ ...p, company_name: e.target.value }))} placeholder="Company name" data-testid="meeting-company-input" />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={meetingForm.notes} onChange={e => setMeetingForm(p => ({ ...p, notes: e.target.value }))} placeholder="Agenda, topics to discuss..." className="min-h-[60px]" data-testid="meeting-notes-input" />
            </div>
            <div className="flex justify-end gap-3 pt-1">
              <Button type="button" variant="outline" onClick={() => setMeetingDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-violet-600 text-white hover:bg-violet-700" data-testid="meeting-submit-btn">{editingMeeting ? 'Update' : 'Schedule'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Meeting Confirm */}
      <AlertDialog open={!!deleteMeetingTarget} onOpenChange={(open) => !open && setDeleteMeetingTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Meeting</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete "{deleteMeetingTarget?.title}"?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteMeeting} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-meeting">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Follow-ups / Notes Dialog — view switched by which chip you clicked */}
      <Dialog open={followupsDialogOpen} onOpenChange={setFollowupsDialogOpen}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {followupsDialogView === 'upcoming' ? <Clock className="w-5 h-5 text-sky-500" /> :
               followupsDialogView === 'notes' ? <Bell className="w-5 h-5 text-teal-500" /> :
               <PhoneForwarded className="w-5 h-5 text-amber-500" />}
              {followupsDialogView === 'upcoming' ? 'Upcoming Follow-ups' :
               followupsDialogView === 'notes' ? 'Notes (manual)' :
               'Follow-ups for Today'}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              {followupsDialogView === 'upcoming' ? 'Scheduled BD follow-ups dated after today' :
               followupsDialogView === 'notes' ? 'Manually-added reminders only (auto-generated follow-ups appear in the other two chips)' :
               'Outreach entries that need a follow-up today or are overdue'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-3 mt-2" style={{ scrollbarWidth: 'thin' }}>
            {(() => {
              const today = new Date().toISOString().split('T')[0];
              const all = (stats?.active_reminders || []);
              let followups;
              if (followupsDialogView === 'upcoming') {
                followups = all.filter(r => (r.auto_type === 'pharma_followup' || r.auto_type === 'account_intro_followup') && r.reminder_date > today);
              } else if (followupsDialogView === 'notes') {
                followups = all.filter(r => !r.auto_type);
              } else {
                followups = all.filter(r => (r.auto_type === 'pharma_followup' || r.auto_type === 'account_intro_followup') && r.reminder_date <= today);
              }
              // Sort upcoming asc by date so soonest shows first
              if (followupsDialogView === 'upcoming') {
                followups = [...followups].sort((a, b) => (a.reminder_date || '').localeCompare(b.reminder_date || ''));
              }
              if (followups.length === 0) {
                const emptyMsg = followupsDialogView === 'upcoming' ? 'No upcoming follow-ups scheduled.'
                  : followupsDialogView === 'notes' ? 'No manual notes yet. Add one via "+ Add Note" on any biotech/pharma profile or in the Notes card below.'
                  : 'All caught up! No follow-ups due today.';
                return (
                  <div className="text-center py-12 flex flex-col items-center" data-testid="followups-empty">
                    <CheckCircle2 className="w-10 h-10 text-emerald-300 mb-3" />
                    <p className="text-sm text-slate-500">{emptyMsg}</p>
                  </div>
                );
              }
              return followups.map(r => {
                const daysUntil = Math.ceil((new Date(r.reminder_date + 'T00:00:00') - new Date(today + 'T00:00:00')) / (1000 * 60 * 60 * 24));
                const isOverdue = daysUntil < 0;
                const isFuture = daysUntil > 0;
                const isAccountIntro = r.auto_type === 'account_intro_followup';
                const isNote = !r.auto_type;
                const typeBadge = isNote ? 'bg-teal-100 text-teal-700' : isAccountIntro ? 'bg-indigo-100 text-indigo-700' : 'bg-sky-100 text-sky-700';
                const typeLabel = isNote ? 'Note' : isAccountIntro ? 'Account Intro Check-in' : 'Pharma Follow-up';
                const cardBg = isNote ? 'bg-teal-50 border-teal-200'
                  : isOverdue ? 'bg-red-50 border-red-200'
                  : isFuture ? 'bg-sky-50 border-sky-200'
                  : 'bg-amber-50 border-amber-200';
                const dayBadge = isNote ? null
                  : isOverdue ? <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">{Math.abs(daysUntil)}d overdue</span>
                  : isFuture ? <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">in {daysUntil}d · {r.reminder_date}</span>
                  : <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Today</span>;
                return (
                  <div key={r.id} className={`p-4 rounded-lg border ${cardBg}`} data-testid={`followup-${r.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${typeBadge}`}>{typeLabel}</span>
                          {dayBadge}
                        </div>
                        <p className="text-sm font-semibold text-slate-900">{r.title}</p>
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          {r.contact_name && <span className="text-xs text-slate-600 font-medium">{r.contact_name}</span>}
                          {r.company_name && <span className="text-xs text-slate-400">{r.company_name}</span>}
                        </div>
                        {r.contact_email && (
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs text-slate-500 font-mono bg-white px-2 py-1 rounded border border-slate-200">{r.contact_email}</span>
                            <button
                              onClick={() => { navigator.clipboard.writeText(r.contact_email); toast.success('Email copied!'); }}
                              className="p-1 rounded hover:bg-white/80 transition-colors"
                              title="Copy email"
                              data-testid={`copy-email-${r.id}`}
                            >
                              <Copy className="w-3.5 h-3.5 text-slate-400 hover:text-slate-700" />
                            </button>
                          </div>
                        )}
                        {r.share_token && (
                          <div className="flex items-center gap-2 mt-2">
                            <button
                              onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/status/${r.share_token}`); toast.success('Status link copied!'); }}
                              className="flex items-center gap-1.5 text-xs font-medium text-teal-600 hover:text-teal-800 bg-teal-50 hover:bg-teal-100 px-3 py-1.5 rounded-md transition-colors"
                              data-testid={`copy-status-link-${r.id}`}
                            >
                              <Copy className="w-3 h-3" />
                              Copy Status Link
                            </button>
                          </div>
                        )}
                        {r.notes && <p className="text-xs text-slate-500 mt-2">{r.notes}</p>}
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <button
                          onClick={() => { handleMarkReminderDone(r.id); }}
                          className="text-[10px] font-medium text-emerald-600 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100 px-2 py-1 rounded transition-colors"
                          data-testid={`done-followup-${r.id}`}
                        >
                          {isNote ? 'Mark Read' : 'Mark Done'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Database Cleanup Dialog */}
      <Dialog open={cleanupOpen} onOpenChange={setCleanupOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }} className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-500" /> Database Cleanup
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Non-target entities found across Biotech & Pharma — CDMOs, CROs, IT firms, consultancies, legal, financial services, and other service providers.
            </DialogDescription>
          </DialogHeader>
          {cleanupScanning ? (
            <div className="flex items-center justify-center py-12 gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
              <span className="text-sm text-slate-500">Scanning all companies across Biotech & Pharma...</span>
            </div>
          ) : cleanupFlagged.length === 0 ? (
            <div className="text-center py-12">
              <Check className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
              <p className="text-sm text-slate-600 font-medium">Database is clean</p>
              <p className="text-xs text-slate-400 mt-1">No non-target entities detected</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500">{cleanupFlagged.length} non-target entit{cleanupFlagged.length === 1 ? 'y' : 'ies'} found</p>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => {
                    if (cleanupSelected.size === cleanupFlagged.length) setCleanupSelected(new Set());
                    else setCleanupSelected(new Set(cleanupFlagged.map(f => f.id)));
                  }} data-testid="cleanup-select-all">
                    {cleanupSelected.size === cleanupFlagged.length ? 'Deselect All' : 'Select All'}
                  </Button>
                  {cleanupSelected.size > 0 && (
                    <Button variant="destructive" size="sm" className="text-xs h-7" onClick={removeNonTargets} disabled={cleanupRemoving} data-testid="cleanup-remove-selected">
                      {cleanupRemoving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
                      Remove {cleanupSelected.size} Selected
                    </Button>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                {cleanupFlagged.map((item) => (
                  <div key={item.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${cleanupSelected.has(item.id) ? 'bg-red-50 border-red-200' : 'bg-white border-slate-100 hover:bg-slate-50'}`}
                    onClick={() => setCleanupSelected(prev => { const next = new Set(prev); if (next.has(item.id)) next.delete(item.id); else next.add(item.id); return next; })}
                    data-testid={`cleanup-item-${item.id}`}>
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 shrink-0 transition-colors ${cleanupSelected.has(item.id) ? 'bg-red-500 border-red-500' : 'border-slate-300'}`}>
                      {cleanupSelected.has(item.id) && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-slate-800">{item.company_name}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${item.collection === 'pharma_contacts' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                          {item.collection === 'pharma_contacts' ? 'Pharma' : 'Biotech'}
                        </span>
                      </div>
                      <p className="text-xs text-amber-600 font-medium mb-1">Matched: {item.reason}</p>
                      <p className="text-xs text-slate-500 line-clamp-2">{item.snippet}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
